import type { SchemaField, SchemaDataType } from "@aqora-io/aqora-data-utils";
import { NaiveDate } from "./date";
import { Duration, Unit as DurationUnit } from "./duration";
import { Time } from "./time";
import { Integer, Float, Decimal } from "./number";
import { TypedNull } from "./typed_null";
import { decodeBase64ToBytes } from "./base64ArrayBuffer";

export enum ScalarType {
  Null = "Null",
  Boolean = "Boolean",
  Integer = "Integer",
  Float = "Float",
  Decimal = "Decimal",
  String = "String",
  Binary = "Binary",
  Date = "Date",
  Time = "Time",
  Timestamp = "Timestamp",
  Duration = "Duration",
}

const get_scalar_type = (data_type: SchemaDataType): ScalarType | undefined => {
  switch (data_type.data_type) {
    case "Null":
      return ScalarType.Null;
    case "Boolean":
      return ScalarType.Boolean;
    case "Int8":
    case "Int16":
    case "Int32":
    case "Int64":
    case "UInt8":
    case "UInt16":
    case "UInt32":
    case "UInt64":
      return ScalarType.Integer;
    case "Float16":
    case "Float32":
    case "Float64":
      return ScalarType.Float;
    case "Decimal128":
    case "Decimal256":
      return ScalarType.Decimal;
    case "Timestamp":
      return ScalarType.Timestamp;
    case "Date32":
    case "Date64":
      return ScalarType.Date;
    case "Time32":
    case "Time64":
      return ScalarType.Time;
    case "Duration":
      return ScalarType.Duration;
    case "Utf8":
    case "LargeUtf8":
    case "Utf8View":
      return ScalarType.String;
    case "Binary":
    case "LargeBinary":
    case "BinaryView":
      return ScalarType.Binary;
  }
};

export type SimpleSchemaFieldBase = { name: string; nullable: boolean };

export enum SimpleSchemaFieldKind {
  Scalar = "Scalar",
  List = "List",
  Struct = "Struct",
  Unsupported = "Unsupported",
}

export type SimpleSchemaScalar = {
  kind: SimpleSchemaFieldKind.Scalar;
  scalar: ScalarType;
};

export type SimpleSchemaList = {
  kind: SimpleSchemaFieldKind.List;
  field: SimpleSchemaField;
};

export type SimpleSchemaStruct = {
  kind: SimpleSchemaFieldKind.Struct;
  fields: SimpleSchemaField[];
};

export type SimpleSchemaUnknown = { kind: SimpleSchemaFieldKind.Unsupported };

export type SimpleSchemaField = SimpleSchemaFieldBase &
  (
    | SimpleSchemaScalar
    | SimpleSchemaList
    | SimpleSchemaStruct
    | SimpleSchemaUnknown
  );

export type SimpleSchema = {
  fields: SimpleSchemaField[];
};

export const get_simple_schema_field = (
  field: SchemaField,
): SimpleSchemaField => {
  const { name, nullable = false } = field;
  const scalar = get_scalar_type(field);
  if (scalar != undefined) {
    return { name, nullable, kind: SimpleSchemaFieldKind.Scalar, scalar };
  }
  switch (field.data_type) {
    case "List":
    case "LargeList":
    case "FixedSizeList":
    case "ListView":
      return {
        name,
        nullable,
        kind: SimpleSchemaFieldKind.List,
        field: get_simple_schema_field(field.field),
      };
    case "Struct":
      return {
        name,
        nullable,
        kind: SimpleSchemaFieldKind.Struct,
        fields: field.fields.map(get_simple_schema_field),
      };
    default:
      return {
        name,
        nullable,
        kind: SimpleSchemaFieldKind.Unsupported,
      };
  }
};

export const get_simple_data_type_name = (
  data_type: SchemaDataType,
): string => {
  const scalar = get_scalar_type(data_type);
  if (scalar != undefined) {
    return scalar;
  }
  switch (data_type.data_type) {
    case "List":
    case "LargeList":
    case "FixedSizeList":
    case "ListView":
      return SimpleSchemaFieldKind.List;
    case "Struct":
      return SimpleSchemaFieldKind.Struct;
    default:
      return data_type.data_type;
  }
};

const TIMESTAMP_FACTOR = {
  Second: 1000,
  Millisecond: 1,
  Microsecond: 1 / 1000,
  Nanosecond: 1 / 1000000,
};

export const parseSchemaField = (data_type: SchemaDataType, value: unknown) => {
  switch (data_type.data_type) {
    case "Timestamp":
      if (value === null) {
        return new TypedNull(Date);
      }
      return new Date((value as number) * TIMESTAMP_FACTOR[data_type.unit]);
    case "Date32":
    case "Date64":
      if (value === null) {
        return new TypedNull(NaiveDate);
      }
      return NaiveDate.fromDate(
        new Date((value as number) * 24 * 60 * 60 * 1000),
      );
    case "Time32":
    case "Time64":
      if (value === null) {
        return new TypedNull(Time);
      }
      return Time.fromDuration(
        new Duration(value as number, DurationUnit.Nanosecond),
      );
    case "Duration":
      if (value === null) {
        return new TypedNull(Duration);
      }
      return new Duration(value as number, DurationUnit[data_type.unit]);
    case "Struct":
      if (value === null) {
        return new TypedNull(Object);
      }
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(
          ([key, value]): [string, unknown] => {
            const field_data_type = data_type.fields.find(
              ({ name }) => name == key,
            );
            if (!field_data_type) {
              return [key, value] as const;
            }
            return [key, parseSchemaField(field_data_type, value)] as const;
          },
        ),
      );
    case "List":
    case "LargeList":
    case "FixedSizeList":
    case "ListView":
      if (value === null) {
        return new TypedNull(Array);
      }
      return (value as Array<unknown>).map((value): unknown => {
        return parseSchemaField(data_type.field, value);
      });
    case "Int8":
    case "Int16":
    case "Int32":
    case "Int64":
    case "UInt8":
    case "UInt16":
    case "UInt32":
    case "UInt64":
      if (value === null) {
        return new TypedNull(Integer);
      }
      return new Integer(value as number);
    case "Float16":
    case "Float32":
    case "Float64":
      if (value === null) {
        return new TypedNull(Float);
      }
      return new Float(value as number);
    case "Decimal128":
    case "Decimal256":
      if (value === null) {
        return new TypedNull(Decimal);
      }
      return new Decimal(value as string, data_type.precision, data_type.scale);
    case "Boolean":
      if (value === null) {
        return new TypedNull(Boolean);
      }
      return value as boolean;
    case "Utf8":
    case "LargeUtf8":
    case "Utf8View":
      if (value === null) {
        return new TypedNull(String);
      }
      return value as string;
    case "Binary":
    case "LargeBinary":
    case "BinaryView":
      if (value === null) {
        return new TypedNull(Uint8Array);
      }
      if (typeof value === "string") {
        return decodeBase64ToBytes(value);
      }
      return value as Uint8Array;
    default:
      return value;
  }
};
